import qs from 'qs';
import { uniq } from 'lodash-es';
import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import UFOLabel from '@atlaskit/react-ufo/label';
import { RouteComponentProps } from 'src/router/types';
import escapeSearchString from 'src/utils/escape-bbql-string';
import scrollTo from 'src/redux/global/actions/scroll-to';
import { SCROLL_TO_TOP_DURATION } from 'src/constants/permalink-scroll';
import {
  loadPullRequests,
  getPullRequestList,
  unloadPullRequests,
} from 'src/redux/pull-request-list';
import {
  getCurrentRepositoryMainBranchName,
  getCurrentRepository,
  getRepositoryBranchingModel,
} from 'src/selectors/repository-selectors';
import withLocation from 'src/router/utils/with-location';
import { getCurrentUser } from 'src/selectors/user-selectors';
import { dismissFlag } from 'src/redux/flags';

import { LoadingPage } from 'src/sections/global/components/loading-page';
import { BucketState } from 'src/types/state';
import Page from './components/page';
import { Filter, FilterBbql, PRFilterState } from './types';
import { buildStatesBBQL } from './utils';

type RouteParams = {
  repositoryOwner: string;
  repositorySlug: string;
};

type Props = RouteComponentProps<RouteParams> & {
  // UUID of the currently logged in user
  currentUuid: string | null | undefined;
  isEmpty: boolean;
  isLoading: boolean;
  isPreRequestState: boolean;
  loadPullRequests: typeof loadPullRequests;
  unloadPullRequests: typeof unloadPullRequests;
  scrollTo: typeof scrollTo;
  dismissFlag: typeof dismissFlag;
};

const preloadPrPage = () => {
  // Assume the user will navigate to the PR page; after this page is mounted
  // we can load assets for the next page to reduce the transition time
  import('src/sections/repository/sections/pull-request/resource-router-route');
};

export class PullRequestList extends React.Component<Props> {
  componentDidMount() {
    window.addEventListener('load', preloadPrPage);
    if (this.props.isPreRequestState) {
      this.loadPullRequests();
    }
  }

  shouldComponentUpdate(nextProps: Props) {
    return (
      nextProps.currentUuid !== this.props.currentUuid ||
      nextProps.location.search !== this.props.location.search ||
      nextProps.isLoading !== this.props.isLoading
    );
  }

  componentDidUpdate(nextProps: Props) {
    if (!this.props.isLoading && !nextProps.isLoading) {
      this.loadPullRequests();
    }
  }

  componentWillUnmount() {
    window.removeEventListener('load', preloadPrPage);
    this.props.unloadPullRequests();
  }

  onFilterChanged = (filter: Filter) => {
    // changing the URL will update the component and load new PRs
    // always set the page back to 1 when the filter changes
    this.props.history.push(this.buildUrl(filter, 1));
  };

  parseQuery = () => {
    const query = qs.parse(this.props.location.search, {
      ignoreQueryPrefix: true,
    });

    const states = query.state as string;

    return {
      prState: (states?.split('+') as PRFilterState[]) || ['OPEN', 'DRAFT'],
      isReviewing: !!query.reviewing,
      search: query.query,
      uuid: query.author,
      branch: query.at,
      sort: query.sort || undefined,
      page: parseInt(query.page as string, 10) || 1,
    };
  };

  buildBBQL = ({
    isReviewing,
    search: inputSearch,
    prState,
    uuid,
    branch,
  }: FilterBbql) => {
    const bbql: string[] = [];

    const statesQuery = buildStatesBBQL(prState);
    if (statesQuery.length > 0) {
      bbql.push(statesQuery);
    }

    if (isReviewing && this.props.currentUuid) {
      bbql.push(`reviewers.uuid="${this.props.currentUuid}"`);
    }

    const search = escapeSearchString(inputSearch || '');

    if (search) {
      const conditions = [`description~"${search}"`, `title~"${search}"`];

      // Extract numbers from the search
      const numerics = search.match(/\d+/g) || [];
      // Try to match them to a pull request ID
      conditions.push(...uniq(numerics).map(id => `id="${id}"`));

      bbql.push(`(${conditions.join(' OR ')})`);
    }
    if (uuid) {
      bbql.push(`author.uuid="${uuid}"`);
    }
    if (branch) {
      bbql.push(`destination.branch.name~"${branch}"`);
    }
    return bbql.join(' AND ');
  };

  loadPullRequests = () => {
    const { repositoryOwner, repositorySlug } = this.props.match.params;
    if (!repositoryOwner || !repositorySlug) {
      return;
    }

    const { page, sort, ...filter } = this.parseQuery();

    this.props.loadPullRequests({
      repositoryOwner,
      repositorySlug,
      bbql: this.buildBBQL(filter as FilterBbql),
      sort: sort as string | undefined,
      page,
    });
  };

  buildUrl = (filter: Filter, page: number) => {
    const { isReviewing, search, prState, uuid, branch, sort } = filter;
    const { search: query, pathname } = this.props.location;
    const oldQuery = qs.parse(query, {
      ignoreQueryPrefix: true,
    });

    const newState = {
      ...oldQuery,
      state: prState.join('+'),
      reviewing: isReviewing ? true : null,
      query: search || null,
      author: uuid || null,
      at: branch || null,
      page: page > 1 ? page : null,
      sort: sort || null,
    };
    const newQuery = qs.stringify(newState, { skipNulls: true });

    return `${pathname}?${newQuery}`;
  };

  scrollToTop = () => {
    this.props.scrollTo({
      targetId: 'root',
      duration: SCROLL_TO_TOP_DURATION,
    });
  };

  render() {
    const { repositoryOwner, repositorySlug } = this.props.match.params;
    const { page, ...filter } = this.parseQuery();
    const getPaginationUrl = this.buildUrl.bind(this, filter);
    const canonicalHref = `${window.location.origin}${this.props.location.pathname}`;

    if (this.props.isLoading) {
      return <LoadingPage interactionName="pull-request-list-route" />;
    }

    return (
      <UFOLabel name="pull-request-list-page">
        <Page
          filter={filter as Filter}
          onFilterChanged={this.onFilterChanged}
          repositoryOwner={repositoryOwner || ''}
          repositorySlug={repositorySlug || ''}
          isEmpty={this.props.isEmpty}
          onLoadPage={this.loadPullRequests}
          onPageChanged={this.scrollToTop}
          page={page}
          canonicalHref={canonicalHref}
          getPaginationUrl={getPaginationUrl}
        />
      </UFOLabel>
    );
  }
}

const mapStateToProps = (state: BucketState) => {
  const currentUser = getCurrentUser(state);
  const branchingModel = getRepositoryBranchingModel(state);
  const branchingModelLoader = branchingModel
    ? branchingModel.isLoading
    : false;
  return {
    currentUuid: currentUser && currentUser.uuid,
    isEmpty: !getCurrentRepositoryMainBranchName(state),
    isLoading: !getCurrentRepository(state) || branchingModelLoader,
    isPreRequestState: getPullRequestList(state).isPreRequestState,
  };
};

const mapDispatchToProps = {
  loadPullRequests,
  scrollTo,
  unloadPullRequests,
  dismissFlag,
};

export default compose(
  withLocation,
  connect(mapStateToProps, mapDispatchToProps)
)(PullRequestList);
